import * as pc from "playcanvas";

const HDR_URL = "/env/autumn_field_puresky.hdr";

/**
 * Image-based environment lighting from an equirectangular HDRI.
 *
 * The arena was lit only by a weak bluish ambient (0.15) + a few
 * short-range omni lights, leaving most of the map dark. An HDRI gives
 * realistic global ambient + reflections (so the skinned characters and
 * surfaces are evenly lit) and — since the arena is open-topped — shows a
 * real sky above the 12 m walls.
 *
 * Pipeline: load the .hdr equirect → prefilter into an env atlas (diffuse
 * + specular IBL) for `scene.envAtlas`, and a cubemap for `scene.skybox`.
 * Filmic ACES tonemapping on the camera keeps the HDR range from blowing
 * out. Fails silently (keeps the existing lights) if the HDR can't load.
 */
export function setupEnvironment(app: pc.AppBase, cameraEntity: pc.Entity): void {
  // Filmic tonemapping so the bright HDR doesn't clip to white. Tonemapping
  // is per-camera in PlayCanvas 2.x.
  const cam = cameraEntity.camera;
  if (cam) cam.toneMapping = pc.TONEMAP_ACES;

  setupSun(app);

  app.assets.loadFromUrl(HDR_URL, "texture", (err, asset) => {
    if (err || !asset) {
      console.warn("[Environment] HDR load failed:", HDR_URL, err);
      return;
    }
    const source = asset.resource as pc.Texture;

    // Diffuse + specular image-based lighting.
    const lighting = pc.EnvLighting.generateLightingSource(source);
    const envAtlas = pc.EnvLighting.generateAtlas(lighting);
    lighting.destroy();
    app.scene.envAtlas = envAtlas;

    // Visible sky above the open-topped walls.
    app.scene.skybox = pc.EnvLighting.generateSkyboxCubemap(source);
    app.scene.skyboxIntensity = 1.0; // raise/lower to taste
    app.scene.skyboxMip = 0; // crisp sky (no blur)

    console.log("[Environment] HDRI lighting + skybox ready");
  });
}

/**
 * Directional "sun" that actually casts shadows (the HDRI only provides
 * ambient/reflections). Everything on the World layer — opponents, cover,
 * walls, and the local shadow body — now casts/receives crisp shadows.
 *
 * Strong + sharp: full shadow intensity, high-res map over a tight
 * distance (dense texels near the player), bright key so the lit ground
 * contrasts hard against the shade.
 */
function setupSun(app: pc.AppBase): void {
  const sun = new pc.Entity("sun");
  sun.addComponent("light", {
    type: "directional",
    color: new pc.Color(1.0, 0.97, 0.88),
    intensity: 2.6,
    castShadows: true,
    shadowType: pc.SHADOW_PCF3,
    // 2048 over a tight 30 m distance keeps shadows crisp near the player
    // at a quarter of the 4096 shadow-pass cost (rendered every frame with
    // skinned casters).
    shadowResolution: 2048,
    shadowDistance: 30,
    shadowBias: 0.015,
    normalOffsetBias: 0.05,
    shadowIntensity: 1.0, // full-strength (darkest the ambient allows)
  });
  // High side angle so shadows have direction without being too long.
  sun.setLocalEulerAngles(52, 28, 0);
  app.root.addChild(sun);
}
